/* Green Heroes — theme JS
   Implements the interactive layer that was previously React state in the design prototype:
   - Intersection-observer fade-ins
   - Sticky nav + mega menu (hover/focus driven)
   - Search overlay, cart drawer toggles
   - FAQ accordions
   - Configurator (live pricing, quality drawer, addon drawer)
   - Compare tray (collection page)
   - Generic tabs (hjælpecenter)
*/

(function () {
  'use strict';

  /* Pris-formatering efter det AKTIVE marked (se window.GH i theme.liquid).
     Var tidligere hardkodet 'da-DK' + ' kr.', så DE/SV-kunder fik danske kroner. */
  const GH_CUR = (window.GH && window.GH.currency) || 'DKK';
  const GH_LOC = (window.GH && window.GH.locale) || 'da';
  const dkk = n => {
    try { return new Intl.NumberFormat(GH_LOC, { style: 'currency', currency: GH_CUR, maximumFractionDigits: 0 }).format(n); }
    catch (e) { return new Intl.NumberFormat(GH_LOC).format(Math.round(n)) + ' ' + GH_CUR; }
  };

  /* ====== AnimateOnScroll ====== */
  function initAnims() {
    const items = document.querySelectorAll('.gh-anim');
    if (!('IntersectionObserver' in window) || items.length === 0) {
      items.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('is-visible'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.08 });
    items.forEach(el => obs.observe(el));
  }

  /* ====== Sticky nav scroll state ====== */
  function initNavScroll() {
    const nav = document.querySelector('.gh-nav');
    if (!nav) return;
    const onScroll = () => nav.classList.toggle('is-scrolled', window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ====== Mega menu ====== */
  function initMegaMenu() {
    const nav = document.querySelector('.gh-nav');
    const menu = document.querySelector('.gh-megamenu');
    const scrim = document.querySelector('.gh-megamenu__scrim');
    if (!nav || !menu) return;

    const links = nav.querySelectorAll('[data-mega]');
    const panels = menu.querySelectorAll('[data-mega-panel]');
    let closeTimer = null;

    function open(id) {
      clearTimeout(closeTimer);
      links.forEach(l => l.classList.toggle('is-active', l.dataset.mega === id));
      panels.forEach(p => p.classList.toggle('is-active', p.dataset.megaPanel === id));
      menu.classList.add('is-open');
      nav.classList.add('is-menu-open');
      scrim && scrim.classList.add('is-open');
    }
    function close() {
      closeTimer = setTimeout(() => {
        menu.classList.remove('is-open');
        nav.classList.remove('is-menu-open');
        scrim && scrim.classList.remove('is-open');
        links.forEach(l => l.classList.remove('is-active'));
      }, 180);
    }

    links.forEach(link => {
      const id = link.dataset.mega;
      if (!id) return;
      link.addEventListener('mouseenter', () => open(id));
      link.addEventListener('focus', () => open(id));
      link.addEventListener('mouseleave', close);
    });
    menu.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    menu.addEventListener('mouseleave', close);
    scrim && scrim.addEventListener('click', () => {
      menu.classList.remove('is-open');
      nav.classList.remove('is-menu-open');
      scrim.classList.remove('is-open');
    });
  }

  /* ====== Mobile nav (hamburger drawer) ====== */
  function initMobileNav() {
    const nav = document.querySelector('.gh-nav');
    const drawer = document.querySelector('[data-mobile-nav]');
    const scrim = document.querySelector('.gh-mobilenav__scrim');
    const openBtn = document.querySelector('[data-mobile-toggle]');
    if (!nav || !drawer || !openBtn) return;
    const closers = document.querySelectorAll('[data-mobile-close]');

    function open() {
      drawer.classList.add('is-open');
      drawer.inert = false;
      scrim && scrim.classList.add('is-open');
      nav.classList.add('is-mobilenav-open');
      openBtn.setAttribute('aria-expanded', 'true');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.inert = true;
      scrim && scrim.classList.remove('is-open');
      nav.classList.remove('is-mobilenav-open');
      openBtn.setAttribute('aria-expanded', 'false');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }
    openBtn.addEventListener('click', () => {
      if (drawer.classList.contains('is-open')) { close(); } else { open(); }
    });
    closers.forEach(c => c.addEventListener('click', close));
    document.addEventListener('keydown', e => { if (e.key === 'Escape') { close(); } });

    drawer.querySelectorAll('[data-acc-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  /* ====== Cart drawer ====== */
  function initCartDrawer() {
    const drawer = document.querySelector('#gh-cart-drawer');
    if (!drawer) return;
    const opener = document.querySelector('[data-cart-toggle]');
    const closer = drawer.querySelector('[data-cart-close]');
    const open = () => { drawer.classList.add('is-open'); drawer.inert = false; document.body.style.overflow = 'hidden'; };
    const close = () => { drawer.classList.remove('is-open'); drawer.inert = true; document.body.style.overflow = ''; };
    opener && opener.addEventListener('click', open);
    closer && closer.addEventListener('click', close);
    drawer.addEventListener('click', e => { if (e.target === drawer) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
    window.addEventListener('cart:open', open);
  }

  /* ====== Search overlay ====== */
  function initSearchOverlay() {
    const overlay = document.querySelector('#gh-search-overlay');
    if (!overlay) return;
    const opener = document.querySelector('[data-search-toggle]');
    const closer = overlay.querySelector('[data-search-close]');
    const input = overlay.querySelector('input[type="search"]');
    const results = overlay.querySelector('#gh-search-results');
    const open = () => { overlay.classList.add('is-open'); document.body.style.overflow = 'hidden'; setTimeout(() => input && input.focus(), 50); };
    const close = () => { overlay.classList.remove('is-open'); document.body.style.overflow = ''; };
    opener && opener.addEventListener('click', open);
    closer && closer.addEventListener('click', close);
    overlay.addEventListener('click', e => { if (e.target === overlay) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });

    // Live predictive search
    if (input && results) {
      let timer = null;
      let lastQuery = '';
      input.addEventListener('input', () => {
        clearTimeout(timer);
        const q = input.value.trim();
        if (q.length < 2) {
          results.innerHTML = 'Skriv i feltet ovenfor for at se produkter.';
          results.style.color = '#6e6e73';
          return;
        }
        if (q === lastQuery) return;
        lastQuery = q;
        results.innerHTML = '<div style="color:#6e6e73;">Søger…</div>';
        timer = setTimeout(() => {
          const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product,page,article&resources[limit]=8&resources[options][unavailable_products]=last`;
          fetch(url, { headers: { 'Accept': 'application/json' } })
            .then(r => r.json())
            .then(data => {
              const products = (data.resources && data.resources.results && data.resources.results.products) || [];
              const pages = (data.resources && data.resources.results && data.resources.results.pages) || [];
              const articles = (data.resources && data.resources.results && data.resources.results.articles) || [];
              if (!products.length && !pages.length && !articles.length) {
                results.innerHTML = `<div style="color:#6e6e73;">Ingen resultater for "${q.replace(/[<>&"]/g, c => ({'<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;'}[c]))}". Prøv et andet søgeord.</div>`;
                return;
              }
              let html = '';
              if (products.length) {
                html += '<div style="display:grid;grid-template-columns:repeat(auto-fill,minmax(180px,1fr));gap:14px;">';
                products.forEach(p => {
                  const price = p.price ? dkk(parseFloat(p.price)) : '';
                  const img = p.featured_image && p.featured_image.url ? p.featured_image.url : (p.image || '');
                  html += `<a href="${p.url}" style="display:block;text-decoration:none;color:inherit;background:#f5f5f7;border-radius:12px;padding:14px;transition:transform .12s ease;">
                    <div style="height:100px;display:flex;align-items:center;justify-content:center;margin-bottom:10px;">${img ? `<img src="${img}" alt="${(p.title||'').replace(/"/g,'&quot;')}" style="max-height:100%;max-width:100%;object-fit:contain;">` : ''}</div>
                    <div style="font-size:13px;font-weight:600;color:#1d1d1f;line-height:1.3;">${p.title || ''}</div>
                    ${price ? `<div style="font-size:13px;color:#08a449;font-weight:600;margin-top:6px;">${price}</div>` : ''}
                  </a>`;
                });
                html += '</div>';
              }
              if (pages.length || articles.length) {
                html += '<div style="margin-top:20px;border-top:1px solid rgba(0,0,0,0.08);padding-top:16px;"><div style="font-size:14px;font-weight:700;color:#1d1d1f;margin-bottom:10px;">Sider og artikler</div>';
                pages.forEach(p => {
                  html += `<a href="${p.url}" style="display:block;padding:6px 0;font-size:14px;color:#2a64d0;text-decoration:none;">${p.title}</a>`;
                });
                articles.forEach(a => {
                  html += `<a href="${a.url}" style="display:block;padding:6px 0;font-size:14px;color:#2a64d0;text-decoration:none;">${a.title}</a>`;
                });
                html += '</div>';
              }
              const seeAllUrl = `/search?q=${encodeURIComponent(q)}`;
              html += `<div style="margin-top:18px;text-align:center;"><a href="${seeAllUrl}" style="display:inline-block;background:#08a449;color:#fff;padding:10px 22px;border-radius:10px;font-size:14px;font-weight:600;text-decoration:none;">Se alle resultater →</a></div>`;
              results.innerHTML = html;
              results.style.color = '';
            })
            .catch(() => {
              results.innerHTML = '<div style="color:#6e6e73;">Søgningen kunne ikke gennemføres. Prøv igen.</div>';
            });
        }, 200);
      });
    }
  }

  /* ====== FAQ accordion ====== */
  function initFAQ() {
    document.querySelectorAll('.gh-faq__item').forEach(item => {
      const btn = item.querySelector('.gh-faq__btn');
      const panel = item.querySelector('.gh-faq__panel');
      if (!btn || !panel) return;
      btn.addEventListener('click', () => {
        const isOpen = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!isOpen));
        panel.style.maxHeight = !isOpen ? panel.scrollHeight + 'px' : '0';
      });
    });
  }

  /* ====== Filter groups (shop) ====== */
  function initFilterGroups() {
    document.querySelectorAll('.gh-filter-group__head').forEach(head => {
      head.addEventListener('click', () => {
        const expanded = head.getAttribute('aria-expanded') !== 'false';
        head.setAttribute('aria-expanded', String(!expanded));
      });
    });
  }

  /* ====== Shop filter + compare tray ====== */
  function initShop() {
    const shop = document.querySelector('#gh-shop');
    if (!shop) return;
    const cards = Array.from(shop.querySelectorAll('.gh-shop-card'));
    const checks = Array.from(shop.querySelectorAll('.gh-shop-filter-check'));
    const priceMax = shop.querySelector('[data-price-max]');
    const countEl = shop.querySelector('[data-shop-count]');
    const tray = document.querySelector('#gh-compare-tray');
    const trayItems = tray ? tray.querySelector('[data-tray-items]') : null;
    const trayCta = tray ? tray.querySelector('[data-tray-cta]') : null;
    let compareSet = new Set();

    function filterPredicate(card) {
      const filters = {};
      checks.forEach(c => {
        if (!c.checked) return;
        const k = c.dataset.filter;
        (filters[k] = filters[k] || []).push(c.value);
      });
      for (const key in filters) {
        const cardValues = (card.dataset[key] || '').split('|');
        if (!filters[key].some(v => cardValues.includes(v))) return false;
      }
      if (priceMax) {
        const cap = Number(priceMax.value || 0);
        if (cap && Number(card.dataset.price) > cap) return false;
      }
      return true;
    }
    function applyFilters() {
      let shown = 0;
      cards.forEach(card => {
        const ok = filterPredicate(card);
        card.style.display = ok ? '' : 'none';
        if (ok) shown++;
      });
      countEl && (countEl.textContent = shown);
      updateAvailableFilters();
    }

    function updateAvailableFilters() {
      // Collect which filter dimensions are currently active
      const activeByDim = {};
      checks.forEach(c => {
        if (c.checked) (activeByDim[c.dataset.filter] = activeByDim[c.dataset.filter] || []).push(c.value);
      });

      checks.forEach(c => {
        const label = c.closest('.gh-filter-check');
        if (!label) return;
        // Checked options are always available (user can uncheck them)
        if (c.checked) { label.classList.remove('is-unavailable'); return; }

        const dim = c.dataset.filter;
        const val = c.value;

        // Check if selecting this option would yield ≥1 result
        const hasMatch = cards.some(card => {
          // Must pass all OTHER active dimensions
          for (const k in activeByDim) {
            if (k === dim) continue;
            const cardVals = (card.dataset[k] || '').split('|');
            if (!activeByDim[k].some(v => cardVals.includes(v))) return false;
          }
          // Price constraint
          if (priceMax) {
            const cap = Number(priceMax.value || 0);
            if (cap && Number(card.dataset.price) > cap) return false;
          }
          // For this dimension: card must include this value
          // (if dim already has active values: OR with them, so options that ADD results stay visible)
          const cardDimVals = (card.dataset[dim] || '').split('|');
          if (activeByDim[dim]) {
            return activeByDim[dim].some(v => cardDimVals.includes(v)) || cardDimVals.includes(val);
          }
          return cardDimVals.includes(val);
        });

        label.classList.toggle('is-unavailable', !hasMatch);
      });
    }
    const priceLabel = shop.querySelector('[data-price-max-label]');

    /* ---- Filter-state i URL'en: et filtreret udvalg kan deles som link ----
       Nøglerne er de samme som checkboxenes data-filter (condition, ram, ...).
       Værdier kan indeholde komma (skærm hedder fx 13,3"), så hvert valg får
       sit EGET param i stedet for en komma-separeret liste:
         ?condition=Meget+flot&ram=16GB&ram=32GB&maxpris=8000
       Stand-værdierne er kanonisk danske uanset sprog (se snippets/grade-key),
       så det samme link virker på dk, de og se.
       Vi rører kun vores egne nøgler — sort_by, page, utm_* m.fl. bevares. */
    const PRICE_PARAM = 'maxpris';
    // Skyderens udgangsværdi = "ingen prisgrænse". Vi læser den FØR URL-state,
    // og bruger den som reference i stedet for .max: browseren klamper value til
    // nærmeste trin på step-gitteret, så .max er ikke altid en nåelig værdi.
    const priceDefault = priceMax ? Number(priceMax.value) : 0;
    function ownParamKeys() {
      const keys = new Set(checks.map(c => c.dataset.filter));
      keys.add(PRICE_PARAM);
      return keys;
    }
    function readStateFromUrl() {
      const qs = new URLSearchParams(window.location.search);
      let any = false;
      checks.forEach(c => {
        if (qs.getAll(c.dataset.filter).includes(c.value)) { c.checked = true; any = true; }
      });
      if (priceMax && qs.has(PRICE_PARAM)) {
        const cap = Number(qs.get(PRICE_PARAM));
        if (cap > 0) {
          priceMax.value = String(Math.min(cap, Number(priceMax.max)));
          if (priceLabel) priceLabel.textContent = dkk(Number(priceMax.value));
          any = true;
        }
      }
      return any;
    }
    function writeStateToUrl() {
      const qs = new URLSearchParams(window.location.search);
      ownParamKeys().forEach(k => qs.delete(k));
      checks.forEach(c => { if (c.checked) qs.append(c.dataset.filter, c.value); });
      // Prisen udelades når skyderen står i udgangsstilling — så filtrerer den intet.
      if (priceMax && Number(priceMax.value) < priceDefault) qs.set(PRICE_PARAM, priceMax.value);
      const q = qs.toString();
      history.replaceState(null, '', q ? `${location.pathname}?${q}` : location.pathname);
    }

    checks.forEach(c => c.addEventListener('change', () => { applyFilters(); writeStateToUrl(); }));
    if (priceMax) {
      priceMax.addEventListener('input', () => {
        if (priceLabel) priceLabel.textContent = dkk(Number(priceMax.value));
        applyFilters();
      });
      // Skriv først URL'en når skyderen slippes — ellers ét history-kald pr. pixel.
      priceMax.addEventListener('change', writeStateToUrl);
    }
    const filterReset = shop.querySelector('[data-filter-reset]');
    if (filterReset) {
      filterReset.addEventListener('click', () => {
        checks.forEach(c => { c.checked = false; });
        if (priceMax) {
          priceMax.value = String(priceDefault);
          if (priceLabel) priceLabel.textContent = dkk(Number(priceMax.value));
        }
        applyFilters();
        writeStateToUrl();
      });
    }

    /* Compare tray */
    const MAX_COMPARE = 4;
    const trayCount = tray ? tray.querySelector('[data-tray-count]') : null;
    const trayProgress = tray ? tray.querySelector('[data-tray-progress]') : null;
    const trayClear = tray ? tray.querySelector('[data-tray-clear]') : null;

    function updateTray() {
      if (!tray) return;
      tray.classList.toggle('is-open', compareSet.size > 0);
      if (trayCount) trayCount.textContent = compareSet.size;
      if (trayProgress) trayProgress.textContent = `${compareSet.size} af ${MAX_COMPARE} valgt`;
      if (!trayItems) return;
      trayItems.innerHTML = '';
      const ids = Array.from(compareSet);
      for (let i = 0; i < MAX_COMPARE; i++) {
        const slot = document.createElement('div');
        if (i < ids.length) {
          slot.className = 'gh-tray__slot';
          const id = ids[i];
          const card = shop.querySelector(`[data-card-id="${id}"]`);
          const img = card ? card.querySelector('img') : null;
          slot.innerHTML = img ? `<img src="${img.src}" alt="">` : '';
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'gh-tray__remove';
          remove.setAttribute('aria-label', 'Fjern fra sammenligning');
          remove.textContent = '×';
          remove.addEventListener('click', () => {
            compareSet.delete(id);
            const toggleBtn = card ? card.querySelector('.gh-shop-card__compare') : null;
            if (toggleBtn) toggleBtn.classList.remove('is-active');
            updateTray();
          });
          slot.appendChild(remove);
        } else {
          slot.className = 'gh-tray__slot gh-tray__slot--empty';
        }
        trayItems.appendChild(slot);
      }
      if (trayCta) {
        trayCta.textContent = `Sammenlign (${compareSet.size}) →`;
        trayCta.href = `${trayCta.dataset.base}?items=${encodeURIComponent(ids.join(','))}`;
      }
    }
    if (trayClear) {
      trayClear.addEventListener('click', () => {
        compareSet.clear();
        cards.forEach(c => {
          const t = c.querySelector('.gh-shop-card__compare');
          if (t) t.classList.remove('is-active');
        });
        updateTray();
      });
    }
    cards.forEach(card => {
      const toggle = card.querySelector('.gh-shop-card__compare');
      if (!toggle) return;
      toggle.addEventListener('click', e => {
        e.preventDefault();
        const id = card.dataset.cardId;
        if (compareSet.has(id)) { compareSet.delete(id); toggle.classList.remove('is-active'); }
        else if (compareSet.size < 4) { compareSet.add(id); toggle.classList.add('is-active'); }
        updateTray();
      });
    });
    // Sæt afkrydsninger fra URL'en FØR første filtrering, så et delt link
    // viser det filtrerede udvalg med det samme.
    const fromUrl = readStateFromUrl();
    applyFilters();
    if (fromUrl) {
      // Åbn de filtergrupper der har et aktivt valg, så modtageren kan se hvad
      // der er filtreret på (grupperne er foldet sammen som standard).
      checks.forEach(c => {
        if (!c.checked) return;
        const group = c.closest('.gh-filter-group');
        const head = group ? group.querySelector('.gh-filter-group__head') : null;
        // Sæt attributten direkte (ikke .click()) — så er vi uafhængige af om
        // initFilterGroups har bundet sin handler endnu.
        if (head) head.setAttribute('aria-expanded', 'true');
      });
    }
  }

  /* ====== Configurator — KUN sticky-bar synlighed ======
     Al pris-, valg- og add-to-cart-logik ligger i sektionens egen inline-JS
     (product-configurator.liquid). Den gamle prototype-logik her overskrev
     Nypris med en falsk total*1.18 ved hvert change-event OG kaprede CTA'en
     — fjernet. Tilføj ALDRIG pris-beregning her igen. */
  function initConfigurator() {
    const root = document.querySelector('#gh-configurator');
    if (!root) return;
    const stickyBar = document.querySelector('#gh-cfg-sticky-bar');
    if (!stickyBar) return;
    const onScroll = () => {
      stickyBar.classList.toggle('is-visible', window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ====== UGC video play-on-click ====== */
  function initUgcVideos() {
    document.querySelectorAll('[data-ugc-play]').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        const tile = btn.closest('.gh-social__video');
        if (!tile) return;
        const video = tile.querySelector('video[data-ugc-video]');
        if (!video) return;
        if (video.paused) {
          video.muted = false;
          video.play().catch(() => { video.muted = true; video.play(); });
          btn.style.display = 'none';
        } else {
          video.pause();
        }
      });
    });
  }

  /* ====== Tabs (hjælpecenter) ====== */
  function initTabs() {
    document.querySelectorAll('[data-tabs]').forEach(group => {
      const nav = group.querySelectorAll('[data-tab]');
      const panes = group.querySelectorAll('[data-tab-pane]');
      nav.forEach(btn => btn.addEventListener('click', () => {
        const id = btn.dataset.tab;
        nav.forEach(b => b.classList.toggle('is-active', b === btn));
        panes.forEach(p => p.classList.toggle('is-active', p.dataset.tabPane === id));
      }));
    });
  }

  /* ====== Compare page renderer ====== */
  function initCompare() {
    const root = document.querySelector('#gh-compare');
    if (!root) return;
    const dataEl = document.querySelector('#gh-compare-data');
    if (!dataEl) return;

    let products = [];
    try { products = JSON.parse(dataEl.textContent || '[]'); } catch (e) { return; }

    const params = new URLSearchParams(window.location.search);
    const itemsParam = (params.get('items') || '').trim();
    if (!itemsParam) return;
    const handles = itemsParam.split(',').map(s => s.trim()).filter(Boolean);
    if (!handles.length) return;

    const byHandle = Object.fromEntries(products.map(p => [p.handle, p]));
    const selected = handles.map(h => byHandle[h]).filter(Boolean);
    if (!selected.length) return;

    const emptyEl = document.querySelector('#gh-compare-empty');
    const resultsEl = document.querySelector('#gh-compare-results');
    if (emptyEl) emptyEl.hidden = true;
    if (resultsEl) resultsEl.hidden = false;

    const headerEl = root.querySelector('[data-compare-header]');
    const rowsEl = root.querySelector('[data-compare-rows]');
    const cols = selected.length;

    if (headerEl) {
      headerEl.style.gridTemplateColumns = `200px repeat(${cols}, 1fr)`;
      headerEl.innerHTML = '<div></div>' + selected.map(p => {
        const price = dkk(p.price / 100);
        const wasHtml = p.compare_at_price && p.compare_at_price > p.price
          ? `<div class="gh-compare__was">${dkk(p.compare_at_price / 100)}</div>` : '';
        const img = p.image ? `<img src="${p.image}" alt="${p.title.replace(/"/g, '&quot;')}" loading="lazy">` : '';
        return `
          <div class="gh-compare__col">
            <div class="gh-compare__col-img">${img}</div>
            <a href="${p.url}" class="gh-compare__col-title">${p.title}</a>
            <div class="gh-compare__col-price">${price}</div>
            ${wasHtml}
            <a href="${p.url}" class="gh-btn gh-btn--primary" style="margin-top:12px; display:inline-block;">Se model</a>
          </div>`;
      }).join('');
    }

    const rows = [
      { label: 'Producent', key: 'producent' },
      { label: 'Model', key: 'model' },
      { label: 'Model nr.', key: 'model_nr' },
      { label: 'Processor', key: 'processor' },
      { label: 'Hukommelse', key: 'hukommelse' },
      { label: 'Harddisk', key: 'harddisk' },
      { label: 'Grafikkort', key: 'grafikkort' },
      { label: 'Skærmstørrelse', key: 'skaerm' },
      { label: 'Mobil bredbånd', key: 'mobil_bredband' },
      { label: 'Farve', key: 'farve' },
      { label: 'Årgang', key: 'aargang' },
      { label: 'Stand', key: 'stand' }
    ];

    if (rowsEl) {
      rowsEl.innerHTML = rows.map(r => {
        const cells = selected.map(p => {
          const v = (p.meta && p.meta[r.key]) || '—';
          return `<div class="gh-compare__cell">${v}</div>`;
        }).join('');
        return `<div class="gh-compare__row" style="grid-template-columns: 200px repeat(${cols}, 1fr);">
          <div class="gh-compare__row-label">${r.label}</div>
          ${cells}
        </div>`;
      }).join('');
    }
  }

  /* ====== Forhandler kreditkortgebyr ====== */
  function initForhandlerFee() {
    var cfg = window.ghConfig || {};
    if (cfg.market !== 'forhandlere') return;
    var FEE_VARIANT = cfg.feeVariantId;
    var FEE_RATE    = 0.0275;
    var VAT_FACTOR  = 1.25; // 25% dansk moms
    var _busy       = false;

    function calcFee(subtotalOre) {
      // 2.75% af beløb FØR moms
      return Math.round(subtotalOre / 100 / VAT_FACTOR * FEE_RATE);
    }

    function updateFee() {
      if (_busy) return Promise.resolve();
      return fetch('/cart.js')
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          var feeKey = null, feeQty = 0, realSubtotal = 0;
          (cart.items || []).forEach(function(item) {
            if (item.variant_id === FEE_VARIANT) {
              feeKey = item.key; feeQty = item.quantity;
            } else {
              realSubtotal += item.line_price;
            }
          });
          var target = realSubtotal > 0 ? Math.max(1, calcFee(realSubtotal)) : 0;
          if (target === feeQty) return;
          _busy = true;
          var body, url = '/cart/change.js';
          if (target === 0) {
            body = JSON.stringify({ id: FEE_VARIANT, quantity: 0 });
          } else if (feeKey) {
            body = JSON.stringify({ id: FEE_VARIANT, quantity: target });
          } else {
            url  = '/cart/add.js';
            body = JSON.stringify({ id: FEE_VARIANT, quantity: target,
              properties: { _kreditkortgebyr: 'true' } });
          }
          return fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: body
          }).then(function() { _busy = false; })
            .catch(function() { _busy = false; });
        });
    }

    window.ghUpdateFee = updateFee;
    updateFee();
  }

  /* ====== Boot ====== */
  document.addEventListener('DOMContentLoaded', () => {
    initAnims();
    initNavScroll();
    initMegaMenu();
    initMobileNav();
    initCartDrawer();
    initSearchOverlay();
    initFAQ();
    initFilterGroups();
    initShop();
    initConfigurator();
    initCompare();
    initUgcVideos();
    initTabs();
    initForhandlerFee();
  });
})();
